import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Server, Smartphone, Monitor, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

export const ServicesSection = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const services = t('services.items', { returnObjects: true });

  const icons = [Server, Smartphone, Monitor, Users];

  return (
    <section
      id="services"
      data-testid="services-section"
      ref={ref}
      className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 bg-[#0F172A]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#F8FAFC] mb-4">
            {t('services.title')}
          </h2>
          <p className="text-[#CBD5E1] text-lg md:text-xl">
            {t('services.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {services.map((service, index) => {
            const Icon = icons[index % icons.length];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  data-testid="content-card"
                  className="bg-[#1A1F3A] rounded-2xl p-6 md:p-8 border border-[#22D3EE]/20 hover:border-[#22D3EE]/50 hover:shadow-[0_0_30px_rgba(34,211,238,0.15)] transition-all duration-300 h-full"
                >
                  <CardContent className="p-0">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex-shrink-0 p-3 rounded-lg bg-[#22D3EE]/10 border border-[#22D3EE]/30">
                        <Icon className="w-6 h-6 text-[#22D3EE]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-[#F8FAFC] mb-2">
                          {service.title}
                        </h3>
                        <p className="text-[#CBD5E1] text-sm leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                    </div>

                    <ul className="space-y-2 mt-4">
                      {service.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="text-sm text-[#CBD5E1] flex items-start"
                        >
                          <span className="text-[#22D3EE] mr-2 mt-1">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
